// ============================================================
// ClientPurgePanel.jsx — Platform-08: the propose-and-verify purge surface
// (Zuber/Alim only in practice — the whole admin portal is allowlisted).
//
// The flow the ADR ratifies (P8-1/P8-5):
//   1. The LIVE eligibility checklist renders as facts, not buttons — the
//      auto-verified items (lifecycle, export, receipt) come from the server
//      and block the commit BY CONSTRUCTION when unsatisfied.
//   2. Propose computes + persists the deletion set. Nothing is deleted.
//   3. Commit = the one manual checklist tick (hand-over confirmed) + the
//      TYPED confirmation phrase. The server re-verifies everything.
//   4. The worker deletes; this panel polls while a purge is executing.
//
// The UI never enforces anything itself — every gate here is presentation;
// the backend re-computes eligibility at propose AND commit AND fire time.
// ============================================================
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Badge from './Badge';
import Modal from './Modal';
import ConfirmDialog from './ConfirmDialog';
import { toast } from './Toaster';
import {
  listClientPurges, proposeClientPurge, cancelClientPurge, commitClientPurge,
} from '../api/clientPurges';
import { formatDateTime } from '../utils/format';
import StatusMessage from './StatusMessage';
import './ClientPurgePanel.css';

const STATUS_BADGE = {
  proposed: { variant: 'info', label: 'Proposed' },
  executing: { variant: 'warning', label: 'Executing' },
  completed: { variant: 'success', label: 'Completed' },
  failed: { variant: 'danger', label: 'Failed' },
  cancelled: { variant: 'neutral', label: 'Cancelled' },
};

const POLL_MS = 10000;

export default function ClientPurgePanel({ clientId, clientName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [proposeOpen, setProposeOpen] = useState(false);
  const [commitTarget, setCommitTarget] = useState(null);
  const [handoverConfirmed, setHandoverConfirmed] = useState(false);
  const [handoverNote, setHandoverNote] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [showTables, setShowTables] = useState(false);
  const pollRef = useRef(null);

  const fetchAll = useCallback(async () => {
    setLoadError(null);
    try {
      setData(await listClientPurges(clientId));
    } catch (err) {
      setLoadError(err?.detail || err?.message || 'Failed to load the purge state');
    } finally {
      setLoading(false);
    }
  }, [clientId]);
  useEffect(() => { setLoading(true); fetchAll(); }, [fetchAll]);

  const items = data?.items || [];
  const executing = items.some((i) => i.status === 'executing');

  // Poll while a purge is executing so completion appears without a refresh.
  useEffect(() => {
    if (executing && !pollRef.current) pollRef.current = setInterval(fetchAll, POLL_MS);
    if (!executing && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [executing, fetchAll]);

  async function handlePropose() {
    setBusy(true);
    try {
      await proposeClientPurge(clientId);
      toast.success('Purge proposed. Review the deletion set below — nothing has been deleted.');
      setProposeOpen(false);
      await fetchAll();
    } catch (err) {
      toast.error(err?.detail || err?.message || 'Failed to propose the purge');
      await fetchAll(); // a 409 carries a fresh checklist — re-render the facts
    } finally { setBusy(false); }
  }

  async function handleCancel(row) {
    setBusy(true);
    try {
      await cancelClientPurge(clientId, row.purgeId);
      toast.success('Proposal cancelled.');
      await fetchAll();
    } catch (err) {
      toast.error(err?.detail || err?.message || 'Failed to cancel the proposal');
    } finally { setBusy(false); }
  }

  function openCommit(row) {
    setCommitTarget(row);
    setHandoverConfirmed(false);
    setHandoverNote('');
    setConfirmText('');
  }

  async function handleCommit() {
    if (!commitTarget) return;
    setBusy(true);
    try {
      await commitClientPurge(clientId, commitTarget.purgeId, {
        confirmation: confirmText,
        handoverConfirmed,
        handoverNote: handoverNote.trim() || undefined,
      });
      toast.success('Purge committed. Deletion is now running in the background.');
      setCommitTarget(null);
      await fetchAll();
    } catch (err) {
      toast.error(err?.detail || err?.message || 'Failed to commit the purge');
      await fetchAll();
    } finally { setBusy(false); }
  }

  if (loading) return <section className="purge-panel"><StatusMessage as="p" variant="loading" className="purge-panel__muted">Loading purge state…</StatusMessage></section>;
  if (loadError) {
    return (
      <section className="purge-panel">
        <h3 className="purge-panel__title">Purge</h3>
        <StatusMessage as="p" variant="error" className="purge-panel__error">{loadError}</StatusMessage>
        <button type="button" className="purge-panel__btn" onClick={fetchAll}>Retry</button>
      </section>
    );
  }

  const eligibility = data?.eligibility || { checklist: [] };
  const phrase = data?.confirmationPhrase || `PURGE CLIENT ${clientId}`;
  const proposal = items.find((i) => i.status === 'proposed');
  const failedCommit = items.find((i) => i.status === 'failed');
  const active = proposal || failedCommit || null;
  const phraseMatches = confirmText === phrase;

  return (
    <section className="purge-panel">
      <h3 className="purge-panel__title">Purge — permanent deletion</h3>
      <p className="purge-panel__hint">
        Removes every record and file this Client holds in the platform, irreversibly, keeping only
        the ratified survivors (billing records, the audit trail, federation config). A purge is
        proposed first (nothing deleted), then committed with a typed confirmation. The path
        is <strong>{eligibility.path === 'trial' ? 'trial (no export precondition)' : 'full client (export + receipt required)'}</strong>.
      </p>

      <div className="purge-panel__checklist">
        {eligibility.checklist.map((item) => (
          <div key={item.key} className={`purge-panel__check ${item.ok ? 'purge-panel__check--ok' : 'purge-panel__check--blocked'}`}>
            <span className="purge-panel__check-mark" aria-hidden="true">{item.ok ? '✓' : '✗'}</span>
            <span>
              <strong>{item.label}</strong>
              {item.detail && <span className="purge-panel__check-detail"> — {item.detail}</span>}
            </span>
          </div>
        ))}
      </div>

      {!active && !executing && (
        <button
          type="button"
          className="purge-panel__btn purge-panel__btn--danger"
          onClick={() => setProposeOpen(true)}
          disabled={busy || !eligibility.eligible}
          title={eligibility.eligible ? undefined : 'The checklist above must be satisfied first.'}
        >
          Propose purge
        </button>
      )}

      {active && (
        <div className="purge-panel__proposal">
          <div className="purge-panel__proposal-head">
            <Badge variant={STATUS_BADGE[active.status]?.variant || 'neutral'}>{STATUS_BADGE[active.status]?.label || active.status}</Badge>
            <span className="purge-panel__muted">
              Proposed {formatDateTime(active.proposedAt)} by {active.proposedBy}
            </span>
          </div>
          {active.status === 'failed' && active.errorDetail && (
            <StatusMessage as="p" variant="error" className="purge-panel__error">Last attempt failed: {active.errorDetail}. Committing again retries.</StatusMessage>
          )}
          {active.deletionSet && (
            <div className="purge-panel__set">
              <p>
                <strong>{active.deletionSet.totalRows}</strong> rows across{' '}
                <strong>{(active.deletionSet.tables || []).filter((t) => t.rowCount > 0).length}</strong> populated tables
                ({(active.deletionSet.tables || []).length} in scope) and{' '}
                <strong>{active.deletionSet.blobCount}</strong> stored files will be permanently deleted.
              </p>
              <button type="button" className="purge-panel__link" onClick={() => setShowTables((v) => !v)}>
                {showTables ? 'Hide the table-by-table set' : 'Review the table-by-table set'}
              </button>
              {showTables && (
                <div className="purge-panel__tables">
                  {(active.deletionSet.tables || []).filter((t) => t.rowCount > 0).map((t) => (
                    <div key={`${t.database}.${t.table}`} className="purge-panel__table-row">
                      <span>{t.database}.{t.table}</span>
                      <span>{t.rowCount}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="purge-panel__actions">
            {active.status === 'proposed' && (
              <button type="button" className="purge-panel__btn" onClick={() => handleCancel(active)} disabled={busy}>
                Cancel proposal
              </button>
            )}
            <button type="button" className="purge-panel__btn purge-panel__btn--danger" onClick={() => openCommit(active)} disabled={busy}>
              {active.status === 'failed' ? 'Commit again (retry)' : 'Commit purge…'}
            </button>
          </div>
        </div>
      )}

      {executing && (
        <p className="purge-panel__executing">
          A purge is executing. This panel refreshes automatically; deletion cannot be stopped once committed.
        </p>
      )}

      {items.length > 0 && (
        <div className="purge-panel__history">
          <h4 className="purge-panel__history-title">History</h4>
          {items.map((row) => (
            <div key={row.purgeId} className="purge-panel__history-row">
              <Badge variant={STATUS_BADGE[row.status]?.variant || 'neutral'}>{STATUS_BADGE[row.status]?.label || row.status}</Badge>
              <span className="purge-panel__muted">
                Proposed {formatDateTime(row.proposedAt)} by {row.proposedBy}
                {row.committedAt && <> · committed {formatDateTime(row.committedAt)} by {row.committedBy}</>}
                {row.completedAt && <> · completed {formatDateTime(row.completedAt)} ({row.rowsDeleted} rows, {row.blobsDeleted} files)</>}
                {row.cancelledAt && <> · cancelled {formatDateTime(row.cancelledAt)} by {row.cancelledBy}</>}
              </span>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        isOpen={proposeOpen}
        onClose={() => setProposeOpen(false)}
        onConfirm={handlePropose}
        title="Propose purge"
        confirmLabel={busy ? 'Computing…' : 'Compute the deletion set'}
        tone="primary"
        isSubmitting={busy}
        message={(
          <>
            <p>
              Compute the full deletion set for <strong>{clientName || `client ${clientId}`}</strong>?
            </p>
            <p>
              This computes and records exactly what a purge would delete, for review.
              <strong> Nothing is deleted by proposing.</strong>
            </p>
          </>
        )}
      />

      <Modal
        isOpen={Boolean(commitTarget)}
        onClose={() => setCommitTarget(null)}
        title="Commit purge — irreversible"
      >
        <p>
          Committing permanently deletes <strong>{clientName || `client ${clientId}`}</strong>'s data.
          There is no cooling-off period and no undo. The server re-verifies the eligibility
          checklist before anything runs.
        </p>
        <label className="purge-panel__tick">
          <input
            type="checkbox"
            checked={handoverConfirmed}
            onChange={(e) => setHandoverConfirmed(e.target.checked)}
          />
          <span>
            I confirm all hand-over obligations outside the platform are complete: the client has
            everything they are owed and we hold written confirmation of receipt.
          </span>
        </label>
        <label className="purge-panel__field">
          <span>Hand-over note (optional — filed on the purge record)</span>
          <textarea
            value={handoverNote}
            onChange={(e) => setHandoverNote(e.target.value)}
            rows={2}
            maxLength={2000}
            placeholder="e.g. Signed receipt from J Smith (RM) filed 12/08/2026."
          />
        </label>
        <label className="purge-panel__field">
          <span>Type <code>{phrase}</code> to confirm</span>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
        </label>
        <div className="purge-panel__actions">
          <button type="button" className="purge-panel__btn" onClick={() => setCommitTarget(null)} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="purge-panel__btn purge-panel__btn--danger"
            onClick={handleCommit}
            disabled={busy || !handoverConfirmed || !phraseMatches}
          >
            {busy ? 'Committing…' : 'Commit purge'}
          </button>
        </div>
      </Modal>
    </section>
  );
}
